// Generated speech: typed lines spoken by Gemini 3.8 Flash TTS on fal (src/server/tts.ts). Each character has a
// base voice (one of Gemini's 30) and a free-text description of how it speaks. Shared by the server and browser.
import type { Emotion } from "./face";

export const GEMINI_VOICES = [
  "Achernar", "Achird", "Algenib", "Algieba", "Alnilam", "Aoede", "Autonoe", "Callirrhoe", "Charon", "Despina",
  "Enceladus", "Erinome", "Fenrir", "Gacrux", "Iapetus", "Kore", "Laomedeia", "Leda", "Orus", "Pulcherrima",
  "Puck", "Rasalgethi", "Sadachbia", "Sadaltager", "Schedar", "Sulafat", "Umbriel", "Vindemiatrix", "Zephyr", "Zubenelgenubi",
] as const;
export type GeminiVoice = (typeof GEMINI_VOICES)[number];

// Google's one-word description of each voice. Only the developer voice lab uses this; the setup screen uses BASE_VOICES.
export const VOICE_HINTS: Record<GeminiVoice, string> = {
  Achernar: "soft", Achird: "friendly", Algenib: "gravelly", Algieba: "smooth", Alnilam: "firm", Aoede: "breezy",
  Autonoe: "bright", Callirrhoe: "easy-going", Charon: "informative", Despina: "smooth", Enceladus: "breathy",
  Erinome: "clear", Fenrir: "excitable", Gacrux: "mature", Iapetus: "clear", Kore: "firm", Laomedeia: "upbeat",
  Leda: "youthful", Orus: "firm", Pulcherrima: "forward", Puck: "upbeat", Rasalgethi: "informative", Sadachbia: "lively",
  Sadaltager: "knowledgeable", Schedar: "even", Sulafat: "warm", Umbriel: "easy-going", Vindemiatrix: "gentle",
  Zephyr: "bright", Zubenelgenubi: "casual",
};

export type Gender = "female" | "male";
export const AGES = ["kid", "teen", "adult", "old"] as const;
export type Age = (typeof AGES)[number];
export const ACCENTS = ["none", "British", "American", "Australian", "Scottish", "Irish", "Southern US", "French", "Indian"] as const;
export type Accent = (typeof ACCENTS)[number];
export const MANNERS = ["cheerful", "grumpy", "shy", "bossy", "posh", "dramatic", "sleepy", "nervous", "excitable", "dry"] as const;
export type Manner = (typeof MANNERS)[number];
export const MAX_MANNERS = 3;
// How fast they talk. The voice model has no speed setting, so this is a sentence in the description too. The model
// takes "slowly" very literally (twice as long), so each sentence was picked by timing one test line (3.3 s with
// none): very slow 7 s, slow 4.7 s, fast 2.1 s, very fast 1.7 s.
export const SPEEDS = ["very slow", "slow", "fast", "very fast"] as const;
export type Speed = (typeof SPEEDS)[number];
export const SPEED_NAMES: Record<Speed, string> = { "very slow": "Very slow", slow: "Slow", fast: "Fast", "very fast": "Very fast" };
const SPEED_SENTENCE: Record<Speed, string> = {
  "very slow": "Speaks slowly.", slow: "Speaks at a relaxed, unhurried pace.", fast: "Speaks a little faster than most people.",
  "very fast": "Speaks extremely fast, rattling the words off like an auctioneer.",
};
export const AGE_NAMES: Record<Age, string> = { kid: "Kid", teen: "Teen", adult: "Adult", old: "Old" };

// Age, accent and manner are one of the offered options or the person's own words (an accent like "South African", a
// manner like "sarcastic"); gender is only ever one of the two. `manner` holds up to MAX_MANNERS offered ones plus at
// most one of their own.
export type Picks = { gender?: Gender; age?: Age | string; accent?: Accent | string; manner?: (Manner | string)[]; speed?: Speed | string };
export const MAX_OWN_PICK = 40; // characters in a pick of your own
export type SpeechVoice = { voice: GeminiVoice; style: string; picks?: Picks };

// The 12 base voices offered on the setup screen, named by how they sound rather than by Gemini's star names.
export const BASE_VOICES: { voice: GeminiVoice; name: string; gender: Gender }[] = [
  { voice: "Zephyr", name: "Bright", gender: "female" }, { voice: "Kore", name: "Firm", gender: "female" },
  { voice: "Leda", name: "Young", gender: "female" }, { voice: "Gacrux", name: "Mature", gender: "female" },
  { voice: "Vindemiatrix", name: "Gentle", gender: "female" }, { voice: "Laomedeia", name: "Upbeat", gender: "female" },
  { voice: "Puck", name: "Upbeat", gender: "male" }, { voice: "Fenrir", name: "Excitable", gender: "male" },
  { voice: "Charon", name: "Steady", gender: "male" }, { voice: "Algenib", name: "Gravelly", gender: "male" },
  { voice: "Umbriel", name: "Easy-going", gender: "male" }, { voice: "Enceladus", name: "Breathy", gender: "male" },
];
export const baseVoiceOf = (v: GeminiVoice) => BASE_VOICES.find(b => b.voice === v);

export const DEFAULT_SPEECH: SpeechVoice = { voice: "Puck", style: "" };
export const MAX_LINE = 400; // characters per typed line
export const MAX_STYLE = 400;

const isVoice = (v: unknown): v is GeminiVoice => GEMINI_VOICES.includes(v as GeminiVoice);
const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined => (list.includes(v as T) ? (v as T) : undefined);
const own = (v: unknown) => (typeof v === "string" ? v.trim().slice(0, MAX_OWN_PICK) || undefined : undefined);
export const isOffered = <T extends string>(list: readonly T[], v: string | undefined): v is T => list.includes(v as T);
function normalisePicks(p: unknown): Picks | undefined {
  if (!p || typeof p !== "object") return undefined;
  const o = p as Record<string, unknown>;
  const list = Array.isArray(o.manner) ? o.manner.map(own).filter((m): m is string => !!m) : [];
  const offered = [...new Set(list.filter(m => isOffered(MANNERS, m)))].slice(0, MAX_MANNERS);
  const mine = list.find(m => !isOffered(MANNERS, m));
  const manner = mine ? [...offered, mine] : offered;
  const out: Picks = {};
  const gender = oneOf(["female", "male"] as const, o.gender), age = oneOf(AGES, o.age) ?? own(o.age), accent = oneOf(ACCENTS, o.accent) ?? own(o.accent), speed = oneOf(SPEEDS, o.speed) ?? own(o.speed);
  if (gender) out.gender = gender;
  if (age) out.age = age;
  if (accent) out.accent = accent;
  if (manner?.length) out.manner = manner;
  if (speed) out.speed = speed;
  return Object.keys(out).length ? out : undefined;
}
export function normaliseSpeech(s: Partial<SpeechVoice> | undefined, fallback = DEFAULT_SPEECH): SpeechVoice {
  const picks = normalisePicks(s?.picks);
  return {
    voice: isVoice(s?.voice) ? s.voice : fallback.voice,
    style: typeof s?.style === "string" ? s.style.trim().slice(0, MAX_STYLE) : fallback.style,
    ...(picks ? { picks } : {}),
  };
}

// "A grumpy, posh old man with a British accent. Speaks slowly." Parts that were not picked are left out; nothing
// picked gives "". The speed is a sentence of its own; one in your own words reads "Speaks in short bursts."
// Your own words go in the same places: an age of your own sits before the noun ("A middle-aged man"), an accent of
// your own is the accent ("with a South African accent").
export function describePicks(picks: Picks | undefined): string {
  if (!picks) return "";
  const t = (v: string | undefined) => v?.trim() || undefined; // your own words may still be being typed
  const p: Picks = { gender: picks.gender, age: t(picks.age), accent: t(picks.accent), manner: picks.manner?.map(m => m.trim()).filter(Boolean), speed: t(picks.speed) };
  const speed = !p.speed ? "" : isOffered(SPEEDS, p.speed) ? SPEED_SENTENCE[p.speed] : `Speaks ${p.speed.replace(/^speaks\s+/i, "").replace(/[.!]+$/, "")}.`;
  const who = describeWho(p);
  return [who, speed].filter(Boolean).join(" ");
}
function describeWho(p: Picks): string {
  const manner = (p.manner ?? []).join(", ");
  const accent = p.accent && p.accent !== "none" ? p.accent.replace(/\s+accent$/i, "") : "";
  if (!p.gender && !p.age && !manner && !accent) return "";
  const young = p.age === "kid" || p.age === "teen";
  const noun = p.gender === "male" ? (young ? "boy" : "man")
    : p.gender === "female" ? (young ? "girl" : "woman")
    : p.age === "kid" ? "kid" : p.age === "teen" ? "teenager" : "person";
  const ageWord = p.age === "old" ? "old" : p.age && !isOffered(AGES, p.age) ? p.age : "";
  const head = [manner, ageWord, noun].filter(Boolean).join(" ");
  const withAccent = accent ? ` with ${/^[AEIOU]/i.test(accent) ? "an" : "a"} ${accent} accent` : "";
  return `${/^[aeiou]/i.test(head) ? "An" : "A"} ${head}${withAccent}.`;
}

// The voice description is the picks sentence, then anything else typed under it. `style` stays the whole thing (what
// the voice is told); these split it for the voice page and put it back together.
export const extraOf = (s: Pick<SpeechVoice, "style" | "picks">): string => {
  const head = describePicks(s.picks);
  return head && s.style.startsWith(head) ? s.style.slice(head.length).trimStart() : s.style;
};
// `extra` is kept as typed (a space typed at the end stays), so this can run on every key press.
export const composeStyle = (picks: Picks | undefined, extra: string): string =>
  [describePicks(picks), extra.trim() ? extra.trimStart() : ""].filter(Boolean).join(" ").slice(0, MAX_STYLE);

// Direction for one line. It says "for this line" so it overrides the character's usual mood for just that line.
export const HOW: Record<Emotion, string> = {
  neutral: "",
  happy: "For this line they are overjoyed: bright, bouncy and beaming, laughing a little as they say it.",
  angry: "For this line they are furious: loud, sharp and clipped, spitting the words out through gritted teeth.",
  sad: "For this line they are heartbroken: low, slow and wobbly, voice cracking, close to tears.",
  surprised: "For this line they are completely shocked: a sharp gasp, high-pitched and breathless, words tumbling out.",
  smug: "For this line they are insufferably smug: slow, silky and self-satisfied, savouring every word with a smirk.",
};
// The style instructions sent with a line: the character's voice description, then the line's emotion.
// If it is too long the character's description is trimmed, never the emotion.
export function styleFor(s: SpeechVoice, emotion: Emotion) {
  const how = HOW[emotion];
  const room = Math.max(0, MAX_STYLE - (how ? how.length + 1 : 0));
  return [s.style.slice(0, room).trim(), how].filter(Boolean).join(" ");
}
